/**
 * MobileInvoicesPage — the workshop's own invoices on a phone.
 *
 * The counterpart to MobileFleetInvoicesPage, which shows one customer their own
 * invoices. This shows the workshop every invoice it has issued, split the same way.
 *
 * ## What is shared, and what is not
 *
 * The chrome (header, pills, pull-to-refresh, the four list states) is
 * {@link MobileListScreen}. The grouping, sorting and counting is
 * {@link useGroupedList}. Both are shared with the fleet screens, because those parts
 * genuinely are identical.
 *
 * The row is not shared. A workshop needs the customer's name and the rego to tell two
 * invoices apart; a fleet customer already knows who they are and needs the balance owed.
 * Forcing one row to serve both would mean a flag and a branch, which is the thing the
 * extraction exists to avoid.
 *
 * ## `paid` comes from the server
 *
 * Never derived here from the status enum. `InvoiceSummaryResponse.paid` is computed by
 * `Invoice.isSettled()`, the single definition shared with the fleet portal — eight
 * status values with three different ways of being settled is not a judgement a phone
 * should be making, and PARTIALLY_PAID in particular is not settled.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileText } from 'lucide-react';
import MobileListScreen from './MobileListScreen';
import useGroupedList from './useGroupedList';
import { fetchInvoices } from '../../store/slices/invoiceSlice';
import { formatAud } from '../../utils/money';

/**
 * One page, deliberately large. The phone list is not paginated — there is no "load
 * more" — so this is the window the filter and sort apply to, and a small page would
 * make "Paid" mean "paid among the most recent eight", which reads as missing data.
 */
const PAGE_SIZE = 100;

const PAYMENT_KEYS = ['unpaid', 'paid'];

const PAYMENT_MODES = [
    { key: 'unpaid', label: 'Not paid' },
    { key: 'paid', label: 'Paid' },
];

/** dd MMM, from an ISO date, without going near a Date and its timezone. */
const shortDate = (iso) => {
    if (!iso) return '';
    const [, m, d] = iso.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${Number(d)} ${months[Number(m) - 1]}`;
};

const InvoiceRow = ({ invoice }) => (
    <li className="border-b border-gray-100 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">
                    {invoice.invoiceNumber}
                </p>
                <p className="truncate text-xs text-gray-500">
                    {invoice.customerName}
                    {invoice.vehicleRego && ` · ${invoice.vehicleRego}`}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-400">
                    Issued {shortDate(invoice.issueDate)}
                </p>
            </div>

            <div className="shrink-0 text-right">
                <p className="text-sm font-semibold text-gray-900">
                    {formatAud(invoice.totalAmount)}
                </p>
                <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    invoice.paid
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-amber-50 text-amber-700'
                }`}>
                    {invoice.paid ? 'Paid' : 'Unpaid'}
                </span>
            </div>
        </div>
    </li>
);

const MobileInvoicesPage = () => {
    const dispatch = useDispatch();
    const invoices = useSelector((s) => s.invoices.invoices);
    const loading = useSelector((s) => s.invoices.invoicesLoading);
    const error = useSelector((s) => s.invoices.invoicesError);

    const [payment, setPayment] = useState('unpaid');

    const load = useCallback(() => {
        dispatch(fetchInvoices({ page: 0, size: PAGE_SIZE }));
    }, [dispatch]);

    useEffect(() => { load(); }, [load]);

    const { groups, counts } = useGroupedList(invoices, {
        keys: PAYMENT_KEYS,
        groupBy: (inv) => (inv.paid ? 'paid' : 'unpaid'),
        sortBy: (inv) => inv.issueDate,
    });

    return (
        <MobileListScreen
            title="Invoices"
            filters={[{
                ariaLabel: 'Payment status',
                options: PAYMENT_MODES.map((m) => ({ ...m, badge: counts[m.key] })),
                value: payment,
                onChange: setPayment,
            }]}
            loading={loading}
            error={error}
            onRefresh={load}
            items={groups[payment]}
            renderItem={(inv) => <InvoiceRow key={inv.uuid} invoice={inv} />}
            empty={{
                icon: FileText,
                // Naming the filter matters: an empty Paid tab with unpaid invoices
                // sitting behind the other pill otherwise reads as "no invoices at all".
                title: payment === 'paid' ? 'Nothing paid yet' : 'Nothing outstanding',
            }}
        />
    );
};

export default MobileInvoicesPage;
